import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { formatDistanceToNowStrict } from 'date-fns';
import { Col, Container, Row } from 'reactstrap/lib';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { getCurrentMonth, getCurrentYear } from 'site-modules/shared/utils/time-util';
import { LEASE_DEALS_BREADCRUMBS } from 'site-modules/lease-deals/utils/breadcrumbs';
import {
  DEFAULT_PAGE_SUBTITLE,
  DEFAULT_PAGE_TITLE,
  EDM_LEASE_DEALS_HERO_CREATIVE_ID,
} from 'site-modules/lease-deals/constants/lease-deals-constants';
import { Breadcrumbs } from 'client/site-modules/shared/components/breadcrumbs/breadcrumbs';
import { interpolateTemplate } from 'client/site-modules/lease-deals/utils/interpolate-template';
import { withCmsContent } from 'site-modules/lease-deals/components/with-cms-content/with-cms-content';
import { LeaseDealsModel, LeaseDealsPaths } from 'client/data/models/lease-deals';

import hiddenGemsHandful from 'client/images/hidden-gems/hidden-gems-handful.png';

import './lease-deals-header.scss';

const HIDDEN_GEMS_HERO_ALT =
  'Hundreds of lease deals. Only a handful of Hidden Gems. We found the rare offers that stand out from the crowds.';

export function LeaseDealsHeaderUI({ urlContext = {}, publishDate = null, content = null, make = undefined }) {
  const { stateName = '', city = '' } = urlContext?.location || {};

  const variables = useMemo(
    () => ({
      month: getCurrentMonth(),
      year: getCurrentYear(),
      state: stateName,
      location: city || stateName,
      make: make || '',
    }),
    [stateName, make, city]
  );

  const heading = interpolateTemplate(content?.title || DEFAULT_PAGE_TITLE, variables);
  const description = interpolateTemplate(content?.subtitle || DEFAULT_PAGE_SUBTITLE, variables);
  const [headingPrefix, ...headingSuffix] = heading.includes(': ') ? heading.split(': ') : [];
  const headingRemainder = headingSuffix.join(': ');

  const dateDifference = useMemo(() => {
    if (!publishDate) return null;

    // Using date-fns because it substantially simplifies the rounded "xx days/hours/minutes ago logic"
    return formatDistanceToNowStrict(new Date(publishDate), {
      addSuffix: true,
      roundingMethod: 'round',
    });
  }, [publishDate]);

  const headingMarkup = heading && (
    <h1 className="heading-1 fw-bold text-white mb-1">
      {headingPrefix ? (
        <>
          <span className="d-block mb-0_5">{headingPrefix}:</span>
          {headingRemainder}
        </>
      ) : (
        heading
      )}
    </h1>
  );

  const descriptionMarkup = description && (
    <p className="text-white mb-0">
      {description}
      {dateDifference && (
        <>
          {' '}
          <i>Updated {dateDifference}.</i>
        </>
      )}
    </p>
  );

  return (
    <div className="lease-deals-header">
      <Container data-tracking-parent={EDM_LEASE_DEALS_HERO_CREATIVE_ID}>
        <Row>
          <Col
            xs={12}
            className="text-center lease-deals-header-content pt-1 pb-0 pb-md-1 text-md-start align-items-start mt-0_5"
          >
            <Breadcrumbs
              data={LEASE_DEALS_BREADCRUMBS}
              itemClassName="text-white"
              className="p-0 mb-0_75 text-white"
              olClassName=""
            />
            <Row className="lease-deals-header-graphic-row align-items-start">
              <Col xs={12} md={7}>
                {headingMarkup}
                <img
                  className={classnames('hidden-gems-hero-graphic', 'd-md-none d-block w-100 mb-1 mt-1_5')}
                  src={hiddenGemsHandful}
                  alt={HIDDEN_GEMS_HERO_ALT}
                />
                {descriptionMarkup}
              </Col>
              <Col xs={12} md={5} className="d-none d-md-block">
                <img
                  className={classnames('hidden-gems-hero-graphic', 'd-block w-100')}
                  src={hiddenGemsHandful}
                  alt={HIDDEN_GEMS_HERO_ALT}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

LeaseDealsHeaderUI.propTypes = {
  publishDate: PropTypes.string,
  urlContext: PropTypes.shape({
    location: PropTypes.shape({
      state: PropTypes.string,
    }),
  }),
  content: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
  }),
  make: PropTypes.string,
};

const stateToPropsConfig = {
  urlContext: bindToPath(URL_CONTEXT_PATH, UrlModel, null, false),
  make: bindToPath(
    LeaseDealsPaths.getRouteFiltersPath(),
    LeaseDealsModel,
    routeFilters => routeFilters?.make ?? null,
    false
  ),
};

export const LeaseDealsHeader = withCmsContent(connectToModel(LeaseDealsHeaderUI, stateToPropsConfig));
